import React from "react";

import { UPLOADED_EMPLOYEE } from "EmployerApplication/Actions";
import { canLinkAccount } from "EmployerApplication/Selectors";
import { api } from "EmployerApplication/urls";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { defaultHandler } from "Utils/http";

import { TitleSmall } from "Cobalt/Header";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import Button from "Common/Button";
import SubmissionErrorWrapper from "Common/forms/SubmissionErrorWrapper";
import Icon from "Common/Icon";

import AddEmployeeForm from "./AddEmployeeForm";

const focusOnErrors = createDecorator();

const CancelButton = ({ onClick, disabled }) => (
  <Button color="gray" onClick={onClick} disabled={disabled} mr={3}>
    Cancel
  </Button>
);

const SubmitButton = ({ disabled, children }) => (
  <Button id="addButton" type="submit" disabled={disabled} aria-label="Confirm add employee">
    {children}
  </Button>
);

const AddEmployeeModal = ({ onHide, show, createEmployeeSuccessCallback }) => {
  const isAccountLinkable = useSelector(canLinkAccount);
  const dispatch = useDispatch();
  const onSubmit = async (values) => {
    const {
      employee,
      employee: { full_name, employee_type, employee_fill },
    } = values;
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "add_employee_modal",
      label: "add employee",
    });

    AnalyticsTracking.trackEvent("add_new_employee_data", {
      employee_name: full_name,
      employee_type,
      employee_fill,
    });

    try {
      const response = await fetchWithCSRFToken(api.addEmployee, {
        method: "POST",
        body: JSON.stringify({
          ...employee,
          company_id_token: companyToken(),
        }),
      });

      await defaultHandler(response);
      const json = await response.json();

      if (createEmployeeSuccessCallback) {
        createEmployeeSuccessCallback(dispatch, json);
      } else {
        dispatch({ type: UPLOADED_EMPLOYEE, employee: json });
      }
      onHide();
    } catch (e) {
      new SubmissionErrorWrapper(e)
        .forForbiddenError(() => {
          window.location.href = "/unexpected-error";
        })
        .orRethrow();
    }
  };
  const handleClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "add_employee_modal",
      label: "cancel",
    });

    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} className="AddEmployeeModal">
      <Form decorators={[focusOnErrors]} onSubmit={onSubmit}>
        {({ handleSubmit, invalid, submitting, values }) => {
          const { employee: { employee_fill, full_name } = {} } = values;
          const employeeFullName = full_name ? full_name.replace(/^\s+|\s+$/g, "") : null;
          return (
            <form onSubmit={handleSubmit}>
              <ModalHeader closeButton>
                <TitleSmall mb={0} mt={0}>
                  Add Employee
                </TitleSmall>
              </ModalHeader>
              <ModalBody>
                <AddEmployeeForm employeeFullName={employeeFullName} employeeWillFillApplication={employee_fill} canLinkAccount={isAccountLinkable} />
              </ModalBody>
              <ModalFooter>
                <CancelButton onClick={handleClose} disabled={submitting} />
                <SubmitButton disabled={invalid || submitting}>
                  {submitting ? (
                    <>
                      <Icon icon="cog" spin mr={3} flavor="solid" />
                      Loading...
                    </>
                  ) : (
                    <>Add Employee</>
                  )}
                </SubmitButton>
              </ModalFooter>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default AddEmployeeModal;
